import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, Mail, Phone, MapPin, Clock, Send, Award, CheckCircle, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const Contact = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                alt="Hyperlink Network Systems"
                className="h-20 w-20 object-contain"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Home</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">About</Link>
              <Link to="/services" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Services</Link>
              <Link to="/projects" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Projects</Link>
              <Link to="/team" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Team</Link>
              <Link to="/contact" className="text-[#18246F] font-semibold">Contact</Link>
              <Button onClick={() => navigate('/contact')} className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white">Let's Connect</Button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">About</Link>
              <Link to="/services" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Services</Link>
              <Link to="/projects" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Projects</Link>
              <Link to="/team" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Team</Link>
              <Link to="/contact" className="block px-4 py-2 text-[#18246F] bg-gray-100 rounded-lg font-semibold">Contact</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#18246F] via-[#293074] to-[#3d4a8f] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">Get In Touch</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-white/90 max-w-3xl">We're here to help and answer any question you might have. We look forward to hearing from you.</p>
        </div>
      </div>

      {/* Contact Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 -mt-20">
            <Card className="bg-white shadow-xl border-t-4 border-[#3C74FF]">
              <CardContent className="p-6 text-center">
                <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-[#3C74FF]" size={28} />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Phone</h3>
                <p className="text-sm text-gray-600">+1 (571) 406-4203</p>
                <p className="text-sm text-gray-600">(+233) 0302-230235</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl border-t-4 border-[#616EB7]">
              <CardContent className="p-6 text-center">
                <div className="bg-[#616EB7]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-[#616EB7]" size={28} />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Email</h3>
                <p className="text-sm text-gray-600">info@hnsgh.com</p>
                <p className="text-sm text-gray-600">support@hnsgh.com</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl border-t-4 border-purple-600">
              <CardContent className="p-6 text-center">
                <div className="bg-purple-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-purple-600" size={28} />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Business Hours</h3>
                <p className="text-sm text-gray-600">Mon - Fri: 8AM - 6PM</p>
                <p className="text-sm text-gray-600">24/7 Support Available</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl border-t-4 border-green-600">
              <CardContent className="p-6 text-center">
                <div className="bg-green-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="text-green-600" size={28} />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Global Reach</h3>
                <p className="text-sm text-gray-600">USA | Ghana</p>
                <p className="text-sm text-gray-600">West Africa | Middle East</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Office Locations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#18246F] mb-4">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">Fill out the form below and our team will get back to you within 24 hours.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3C74FF] focus:border-transparent"
                    placeholder="Your Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3C74FF] focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3C74FF] focus:border-transparent"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3C74FF] focus:border-transparent"
                    placeholder="Tell us more about your project..."
                  ></textarea>
                </div>

                <Button type="submit" className="w-full bg-[#3C74FF] hover:bg-[#2d5fd9] text-white py-6 text-lg">
                  <Send className="mr-2" size={20} />
                  Send Message
                </Button>
              </form>
            </div>

            {/* Office Locations */}
            <div>
              <h2 className="text-3xl font-bold text-[#18246F] mb-4">Our Offices</h2>
              <p className="text-gray-600 mb-8">Visit us at our locations or schedule a virtual meeting.</p>

              {/* USA Office */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-6 border-l-4 border-[#3C74FF]">
                <div className="flex items-start space-x-4 mb-4">
                  <MapPin className="text-[#3C74FF] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-bold text-[#18246F] text-lg mb-2">USA Office</h3>
                    <p className="text-gray-700 text-sm mb-1">10432 Balls Ford Rd, Suite 300</p>
                    <p className="text-gray-700 text-sm mb-3">Manassas, VA 20109</p>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600"><strong>Phone:</strong> +1 (571) 406-4203</p>
                      <p className="text-sm text-gray-600"><strong>Email:</strong> info@hnsgh.com</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ghana Office */}
              <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#616EB7]">
                <div className="flex items-start space-x-4 mb-4">
                  <MapPin className="text-[#616EB7] flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-bold text-[#18246F] text-lg mb-2">Ghana Office</h3>
                    <p className="text-gray-700 text-sm mb-1">Ghana Science and Museum, Block 8</p>
                    <p className="text-gray-700 text-sm mb-1">Barnes Road, Adabraka</p>
                    <p className="text-gray-700 text-sm mb-3">Accra, Ghana - West Africa</p>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600"><strong>Phone:</strong> (+233) 0302-230235</p>
                      <p className="text-sm text-gray-600"><strong>Fax:</strong> (+233) 0302-230261</p>
                      <p className="text-sm text-gray-600"><strong>Email:</strong> info@hnsgh.com</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="mt-8 bg-gradient-to-br from-[#3C74FF] to-[#616EB7] rounded-xl p-6 text-white">
                <h3 className="font-bold text-xl mb-4">Why Choose Us</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <CheckCircle size={20} className="mr-3 flex-shrink-0" />
                    <span className="text-sm">20+ Years of Excellence</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="mr-3 flex-shrink-0" />
                    <span className="text-sm">150+ Successful Projects</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="mr-3 flex-shrink-0" />
                    <span className="text-sm">24/7 Support Available</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="mr-3 flex-shrink-0" />
                    <span className="text-sm">Global Presence</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18246F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                  alt="HNS Logo"
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">HNS</h3>
                  <p className="text-xs text-gray-300">...technology with infinite solutions</p>
                </div>
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Leading the ICT industry with reliable information systems solutions with over 20 years of excellence in technology delivery.
              </p>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Award size={16} />
                <span>Trusted by 50+ Organizations</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-white transition-colors">Projects</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Network Security</li>
                <li>Cloud Management</li>
                <li>Software Development</li>
                <li>VoIP Solutions</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>info@hnsgh.com</span>
                </li>
                <li className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>Accra, Ghana | Manassas, VA USA</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hyperlink Network Systems Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a 
        href="tel:+15714064203"
        className="fixed bottom-8 left-8 bg-[#3C74FF] hover:bg-[#2d5fd9] text-white rounded-full p-4 shadow-2xl z-50 flex items-center gap-3 group transition-all duration-300 hover:scale-110"
      >
        <Phone size={24} className="animate-pulse" />
        <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap">
          +1 (571) 406-4203
        </span>
      </a>
    </div>
  );
};

export default Contact;
